import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Truck, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmationPage = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/track/${orderNumber}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 pb-24 chaleur-container">
        <div className="max-w-2xl mx-auto animate-pulse space-y-6">
          <div className="h-8 bg-gray-100 rounded w-64 mx-auto" />
          <div className="h-40 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="pt-32 pb-24 chaleur-container text-center">
        <h1 className="text-2xl mb-4">Order not found</h1>
        <Button asChild className="rounded-none">
          <Link to="/shop">Continue Shopping</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="order-confirmation">
      <div className="chaleur-container">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-light mb-2">Thank You for Your Order!</h1>
            <p className="text-muted-foreground">
              Order #{order.order_number} has been confirmed
            </p>
          </div>

          {/* Order Details Card */}
          <div className="border border-gray-200 divide-y divide-gray-200">
            {/* Order Info */}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-primary" />
                <h2 className="font-medium">Order Details</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Order Number:</span>
                  <p className="font-medium">{order.order_number}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Order Date:</span>
                  <p className="font-medium">{new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p className="font-medium capitalize">{order.status}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Payment:</span>
                  <p className="font-medium capitalize">{order.payment_status}</p>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="p-6">
              <h3 className="font-medium mb-4">Items Ordered</h3>
              <div className="space-y-4">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <img 
                      src={item.image} 
                      alt={item.product_name}
                      className="w-16 h-16 object-cover bg-gray-100"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-muted-foreground">{item.variant_name}</p>
                      <p className="text-sm">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">₹{item.line_total?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="p-6 bg-gray-50">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{order.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>₹{order.shipping_cost?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax (GST)</span>
                  <span>₹{order.tax?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-medium pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>₹{order.total?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-primary" />
                <h3 className="font-medium">Shipping Address</h3>
              </div>
              <div className="text-sm space-y-1">
                <p className="font-medium">
                  {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                </p>
                <p>{order.shipping_address?.address_line1}</p>
                {order.shipping_address?.address_line2 && (
                  <p>{order.shipping_address?.address_line2}</p>
                )}
                <p>
                  {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postal_code}
                </p>
                <p>{order.shipping_address?.country}</p>
                <p className="flex items-center gap-2 mt-2">
                  <Phone className="w-4 h-4" />
                  {order.shipping_address?.phone}
                </p>
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="mt-8 p-6 bg-amber-50 border border-amber-200">
            <h3 className="font-medium mb-3">What's Next?</h3>
            <ul className="text-sm space-y-2 text-amber-900">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                <span>You'll receive an email confirmation shortly at your provided email address.</span>
              </li>
              <li className="flex items-start gap-2">
                <Package className="w-4 h-4 mt-0.5 shrink-0" />
                <span>We'll start preparing your order for shipment.</span>
              </li>
              <li className="flex items-start gap-2">
                <Truck className="w-4 h-4 mt-0.5 shrink-0" />
                <span>You'll receive tracking information once your order ships.</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild variant="outline" className="rounded-none">
              <Link to={`/track-order?order=${order.order_number}`}>Track Order</Link>
            </Button>
            <Button asChild className="rounded-none">
              <Link to="/shop">Continue Shopping</Link>
            </Button>
          </div>

          {/* Create Account CTA for guests */}
          {order.is_guest_order && (
            <div className="mt-8 p-6 border border-primary bg-primary/5 text-center">
              <h3 className="font-medium mb-2">Create an Account</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Sign up to track your orders, earn loyalty points, and checkout faster next time!
              </p>
              <Button asChild variant="outline" className="rounded-none">
                <Link to="/register">Create Account</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
